<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref } from "vue";
import { recordIdFromResponse } from "./imudeiResponse";
import { useImudeiRuntimeModel } from "./useImudeiRuntimeModel";

const emit = defineEmits(["navigate"]);
const realData = inject("imudeiRealData");
const { error, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => "visits"),
    screen: computed(() => "create"),
  }),
);

const searchQuery = ref("");

const asArray = (value) => (Array.isArray(value) ? value : []);
const firstValue = (value, paths, fallback = "") => {
  const result = paths
    .map((path) => path.split(".").reduce((item, key) => item?.[key], value))
    .find((item) => item !== undefined && item !== null && item !== "");

  return result ?? fallback;
};
const initials = (value) =>
  String(value || "—")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((item) => item[0])
    .join("")
    .toLocaleUpperCase("pt-BR");
const imageUrlFrom = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return imageUrlFrom(value[0]);
  if (!value || typeof value !== "object") return "";

  return imageUrlFrom(
    firstValue(value, ["url", "src", "image_url", "imageUrl", "preview_url"]),
  );
};
const optionLabel = (value) => {
  const text = String(value || "").replaceAll("_", " ");
  return text ? `${text[0].toLocaleUpperCase("pt-BR")}${text.slice(1)}` : "—";
};
const formatDateTime = (value) => {
  if (!value) return "Aguardando definição";

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
};
const statusTone = (value) => {
  const status = String(value || "").toLocaleLowerCase("pt-BR");
  if (/dispon|ativo|livre/.test(status)) return "text-emerald-600";
  if (/negoci|reserv|pend/.test(status)) return "text-amber-600";
  if (/inativ|cancel|recus/.test(status)) return "text-rose-600";
  return "text-[#657197]";
};

const createOptions = computed(() => realData.data.value?.createOptions || {});
const properties = computed(() => asArray(createOptions.value.properties));
const contacts = computed(() => asArray(createOptions.value.contacts));
const fieldByKey = computed(() =>
  Object.fromEntries(model.fields.map((field) => [field.key, field])),
);
const propertyField = computed(() => fieldByKey.value.imovel_id);
const contactField = computed(() => fieldByKey.value.contact_id);
const scheduleFields = computed(() =>
  model.fields.filter((field) =>
    ["scheduled_at", "ends_at"].includes(field.key),
  ),
);
const configurationFields = computed(() =>
  model.fields.filter(
    (field) =>
      !["imovel_id", "contact_id", "scheduled_at", "ends_at"].includes(
        field.key,
      ),
  ),
);
const requiredFields = computed(() =>
  model.fields.filter((field) => field.required),
);
const requiredFieldsComplete = computed(() =>
  requiredFields.value.every((field) =>
    String(model.form[field.key] || "").trim(),
  ),
);
const creationError = computed(() => model.localError || error.value || "");
const canCreateVisit = computed(
  () => model.canCreate && requiredFieldsComplete.value,
);

const titleFor = (record) =>
  firstValue(record, ["titulo", "title", "name", "reference", "code"]);
const addressFor = (record) =>
  firstValue(record, [
    "address.full_address",
    "endereco_completo",
    "endereco",
    "address.street",
    "address",
    "location",
  ]);
const imageFor = (record) =>
  imageUrlFrom(
    firstValue(record, [
      "cover_url",
      "image_url",
      "thumbnail",
      "midias",
      "media",
      "images",
      "imovel.cover_url",
      "imovel.image_url",
      "property.cover_url",
      "property.image_url",
    ]),
  );
const propertyFacts = (record) =>
  [
    firstValue(record, ["bedrooms", "quartos", "dormitorios"]),
    firstValue(record, ["bathrooms", "banheiros"]),
    firstValue(record, ["parking_spaces", "vagas", "garages"]),
    firstValue(record, ["area", "area_total", "area_privativa", "metragem"]),
  ]
    .filter((value) => value !== "")
    .join(" · ");
const propertyStatus = (record) =>
  firstValue(record, [
    "status.label",
    "status",
    "availability_status",
    "primary_listing.status",
  ]);
const propertyCards = computed(() =>
  properties.value
    .map((record) => ({
      address: addressFor(record),
      facts: propertyFacts(record),
      id: String(firstValue(record, ["id", "imovel_id", "property_id"])),
      image: imageFor(record),
      status: propertyStatus(record),
      title: titleFor(record),
    }))
    .filter((record) => record.id && record.id !== "undefined"),
);
const filteredProperties = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase("pt-BR");
  if (!query) return propertyCards.value;

  return propertyCards.value.filter((record) =>
    `${record.title} ${record.address} ${record.status}`
      .toLocaleLowerCase("pt-BR")
      .includes(query),
  );
});
const unavailableSlots = (kind, count, label) =>
  Array.from({ length: count }, (_, index) => ({
    id: `${kind}-${index}`,
    kind,
    label,
  }));
const propertySlots = computed(() => [
  ...filteredProperties.value.slice(0, 6).map((record) => ({
    ...record,
    kind: "property",
  })),
  ...unavailableSlots(
    "property-unavailable",
    Math.max(0, 6 - filteredProperties.value.length),
    searchQuery.value
      ? "Nenhum imóvel encontrado"
      : "Imóvel não retornado pela API",
  ),
]);
const contactCards = computed(() =>
  contacts.value
    .map((contact) => ({
      id: String(contact.id),
      image: imageUrlFrom(
        firstValue(contact, [
          "thumbnail",
          "avatar_url",
          "contact.thumbnail",
          "contact.avatar_url",
        ]),
      ),
      name: firstValue(contact, ["name", "full_name", "contact.name"]),
      subtitle: firstValue(contact, [
        "phone_number",
        "email",
        "contact.phone_number",
        "contact.email",
      ]),
    }))
    .filter((contact) => contact.id && contact.id !== "undefined"),
);
const contactSlots = computed(() => [
  ...contactCards.value
    .slice(0, 3)
    .map((contact) => ({ ...contact, kind: "contact" })),
  ...unavailableSlots(
    "contact-unavailable",
    Math.max(0, 3 - contactCards.value.length),
    "Contato não retornado pela API",
  ),
]);
const selectedProperty = computed(() =>
  propertyCards.value.find(
    (property) => String(property.id) === String(model.form.imovel_id),
  ),
);
const selectedContact = computed(() =>
  contactCards.value.find(
    (contact) => String(contact.id) === String(model.form.contact_id),
  ),
);
const steps = computed(() => [
  {
    complete: Boolean(model.form.imovel_id),
    label: "Imóvel",
    subtitle: "Escolha o imóvel",
  },
  {
    complete: Boolean(model.form.contact_id),
    label: "Cliente",
    subtitle: "Quem vai participar",
  },
  {
    complete: scheduleFields.value.every((field) => model.form[field.key]),
    label: "Data e hora",
    subtitle: "Quando será",
  },
  {
    complete: Boolean(model.notice),
    label: "Confirmação",
    subtitle: "Revise e agende",
  },
]);
const activeStep = computed(() => {
  const nextStep = steps.value.findIndex((step) => !step.complete);
  return nextStep === -1 ? steps.value.length - 1 : nextStep;
});
const recentVisits = computed(() => model.records.slice(0, 4));
const visitMetrics = computed(() => model.metrics.slice(0, 4));

const selectProperty = (property) => {
  model.form.imovel_id = property.id;
};
const selectContact = (contact) => {
  model.form.contact_id = contact.id;
};
const createVisit = async () => {
  if (!canCreateVisit.value) return;

  const response = await model.create();
  if (!response) return;

  const id =
    recordIdFromResponse(response) ||
    firstValue(response, ["visita.id", "visit.id"]);
  if (id) {
    emit("navigate", { id, screen: "detail" });
    return;
  }

  emit("navigate", "dashboard");
};
</script>

<template>
  <main class="w-full pb-20 pl-8 pr-4 pt-4 text-[#101958]">
    <div class="grid grid-cols-[minmax(0,1fr)_296px] items-start gap-4">
      <section class="-mt-4 min-w-0 space-y-[18px]">
        <header
          data-imudei-visual-id="visit-create-header"
          class="flex h-[55px] items-start justify-between"
        >
          <div class="relative top-[3px] -ml-8">
            <h1 class="text-[24px] font-bold tracking-[-0.04em]">
              Agendar visita
            </h1>
            <p class="mt-1 text-[11px] text-[#56658e]">
              Siga os passos para criar uma visita com os dados disponíveis
            </p>
          </div>
          <article
            class="relative -left-px -top-px h-[65px] w-[260px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white px-3 py-2 shadow-[0_2px_10px_rgba(52,27,102,0.03)]"
          >
            <h2 class="flex items-center gap-1 text-[10px] font-bold">
              <span class="i-lucide-circle-help size-3 text-[#1e2a75]" /> Como
              funciona
            </h2>
            <div class="mt-2 grid grid-cols-3 gap-1 text-[7px] font-semibold">
              <span class="truncate text-emerald-600">Agendar <b>—</b></span><span class="truncate text-amber-600">Confirmar <b>—</b></span><span class="truncate text-violet-600">Resultado <b>—</b></span>
            </div>
          </article>
        </header>

        <section
          data-imudei-visual-id="visit-create-steps"
          class="relative top-px -ml-[33px] flex h-[67px] w-[calc(100%+2rem)] items-center rounded-xl border border-[#eeeaf5] bg-white px-5 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
        >
          <template v-for="(step, index) in steps" :key="step.label">
            <div class="flex min-w-0 flex-1 items-center gap-3">
              <span
                class="grid size-8 shrink-0 place-items-center rounded-full text-[12px] font-bold"
                :class="
                  index === activeStep
                    ? 'bg-[#5120cf] text-white'
                    : step.complete
                      ? 'bg-[#eee6ff] text-[#5520c8]'
                      : 'bg-[#f6f3f8] text-[#31406d]'
                "
                >{{ index + 1 }}</span>
              <span class="min-w-0"><b
                  class="block text-[11px] font-semibold"
                  :class="index === activeStep ? 'text-[#4f1dc5]' : ''"
                  >{{ step.label }}</b><small
                  class="mt-0.5 block truncate text-[9px] text-[#647096]"
                  >{{ step.subtitle }}</small></span>
            </div>
            <i v-if="index < 3" class="mx-2 h-px flex-1 bg-[#eeeaf3]" />
          </template>
        </section>

        <div
          class="relative -top-px -ml-[33px] grid w-[calc(100%+2rem)] grid-cols-[minmax(0,1fr)_293px] items-start gap-[14px]"
        >
          <section
            data-imudei-visual-id="visit-create-catalog"
            class="h-[555px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white px-4 pb-4 pt-2.5 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
          >
            <header>
              <div>
                <h2 class="text-[12px] font-bold">Buscar imóvel</h2>
                <p class="mt-1 text-[9px] text-[#657197]">
                  Encontre por endereço, bairro, código ou referência
                </p>
              </div>
            </header>
            <label
              class="mt-1.5 flex h-8 w-full items-center gap-2 rounded-lg border border-[#e7e2ee] px-3 text-[9px] text-[#a2a9bc]"
              ><span class="i-lucide-search size-3" /><input
                v-model="searchQuery"
                class="w-full border-0 bg-transparent p-0 text-[9px] outline-none placeholder:text-[#a2a9bc]"
                placeholder="Buscar por endereço"
            /></label>
            <h3 class="mt-3 text-[11px] font-bold">Imóveis disponíveis</h3>
            <div class="mt-2 grid grid-cols-3 gap-4">
              <article
                v-for="(property, index) in propertySlots"
                :key="property.id"
                class="h-[188px] overflow-hidden rounded-xl border bg-white"
                :class="
                  property.kind === 'property' &&
                  String(property.id) === String(model.form.imovel_id)
                    ? 'border-[#7645e9] ring-1 ring-[#cbb9ff]'
                    : 'border-[#ebe7f2]'
                "
              >
                <button
                  v-if="property.kind === 'property'"
                  type="button"
                  class="flex h-full w-full flex-col text-left"
                  @click="selectProperty(property)"
                >
                  <span
                    class="relative block h-[92px] shrink-0 overflow-hidden bg-gradient-to-br from-[#eee8fb] to-[#f8f5fb]"
                  >
                    <img
                      v-if="property.image"
                      :src="property.image"
                      :alt="property.title"
                      class="h-full w-full object-cover"
                    />
                    <span
                      v-else
                      class="grid h-full place-items-center text-[#6940c6]"
                      ><span class="i-lucide-building-2 size-7" /></span>
                    <span
                      class="absolute right-2 top-2 grid size-6 place-items-center rounded-full bg-white/90 text-[#66739a]"
                      ><span
                        class="i-lucide-heart size-3.5"
                        :class="
                          String(property.id) === String(model.form.imovel_id)
                            ? 'fill-[#6330d7] text-[#6330d7]'
                            : ''
                        "
                    /></span>
                    <span
                      v-if="
                        String(property.id) === String(model.form.imovel_id)
                      "
                      class="absolute right-2 top-2 grid size-6 place-items-center rounded-full bg-[#6328d2] text-white"
                      ><span class="i-lucide-check size-3.5" /></span>
                  </span>
                  <span class="flex flex-1 flex-col px-3 pb-3 pt-2">
                    <strong class="block truncate text-[10px]">{{
                      property.title || `Imóvel #${property.id}`
                    }}</strong>
                    <small
                      class="mt-1 block truncate text-[8px] text-[#657197]"
                      >{{
                        property.address || "Endereço não retornado pela API"
                      }}</small>
                    <small
                      v-if="property.facts"
                      class="mt-2 block truncate text-[8px] text-[#536187]"
                      ><span
                        class="i-lucide-house mr-1 inline-block size-2.5"
                      />{{ property.facts }}</small>
                    <span class="mt-auto flex items-center justify-between"><small
                        class="text-[8px] font-semibold"
                        :class="statusTone(property.status)"
                        >{{ property.status || "Status não retornado" }}</small><small
                        class="rounded bg-[#f3f0f8] px-1.5 py-0.5 text-[7px] text-[#687394]"
                        >API</small></span>
                  </span>
                </button>
                <div
                  v-else
                  class="flex h-full flex-col items-center justify-center bg-[#fcfbfd] p-3 text-center"
                >
                  <span
                    class="grid size-9 place-items-center rounded-full bg-[#f2eef7] text-[#9aa3b8]"
                    ><span class="i-lucide-building-2 size-4" /></span>
                  <strong class="mt-3 text-[9px] text-[#687394]">{{
                    property.label
                  }}</strong>
                  <label
                    v-if="index === 0 && propertyField"
                    class="mt-3 w-full text-left"
                    ><span class="block text-[8px] text-[#7b85a2]">{{
                      propertyField.label
                    }}</span><input
                      v-model="model.form.imovel_id"
                      type="number"
                      min="1"
                      class="mt-1 h-7 w-full rounded-lg border border-[#e7e2ee] bg-white px-2 text-[8px] text-[#26376f] outline-none focus:border-[#8355e4]"
                      placeholder="Informe o ID"
                  /></label>
                </div>
              </article>
            </div>
            <p
              class="mt-2 flex h-7 items-center justify-center rounded-lg border border-[#eeeaf4] text-center text-[8px] text-[#7c86a2]"
            >
              {{
                properties.length
                  ? "Mostrando imóveis retornados pela API."
                  : "A API não retornou imóveis para esta criação."
              }}
            </p>
          </section>

          <section
            data-imudei-visual-id="visit-create-participants"
            class="h-[555px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white px-4 pb-4 pt-2.5 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
          >
            <h2 class="text-[12px] font-bold">Participantes da visita</h2>
            <p class="mt-1 text-[9px] text-[#657197]">
              Selecione o contato retornado para esta visita
            </p>
            <div class="mt-2 space-y-2">
              <article
                v-for="(contact, index) in contactSlots"
                :key="contact.id"
                class="h-[64px] min-h-0 rounded-xl border border-[#f0ebf4] px-3 py-2 shadow-[0_2px_8px_rgba(52,27,102,0.025)]"
                :class="
                  contact.kind === 'contact' &&
                  String(contact.id) === String(model.form.contact_id)
                    ? 'border-[#7d38db] bg-[#faf8ff]'
                    : ''
                "
              >
                <button
                  v-if="contact.kind === 'contact'"
                  type="button"
                  class="flex w-full items-center gap-3 text-left"
                  @click="selectContact(contact)"
                >
                  <img
                    v-if="contact.image"
                    :src="contact.image"
                    :alt="contact.name"
                    class="size-9 rounded-full object-cover ring-2 ring-[#7d38db] ring-offset-2"
                  />
                  <span
                    v-else
                    class="grid size-9 place-items-center rounded-full bg-[#efe9ff] text-[10px] font-bold text-[#5c25cb] ring-2 ring-[#7d38db] ring-offset-2"
                    >{{ initials(contact.name) }}</span>
                  <span class="min-w-0 flex-1"><strong class="block truncate text-[9px]">{{
                      contact.name || `Contato #${contact.id}`
                    }}</strong><small
                      class="mt-1 block truncate text-[8px] text-[#6d789a]"
                      >{{
                        contact.subtitle || "Contato retornado pela API"
                      }}</small></span>
                  <span class="text-[12px] text-amber-500">{{
                    String(contact.id) === String(model.form.contact_id)
                      ? "✓"
                      : "✦"
                  }}</span>
                </button>
                <label
                  v-else-if="index === 0 && contactField"
                  class="block"
                  ><span
                    class="flex items-center gap-2 text-[8px] text-[#7b85a2]"
                    ><span class="i-lucide-user-round size-3" />{{
                      contact.label
                    }}</span><input
                    v-model="model.form.contact_id"
                    type="number"
                    min="1"
                    class="mt-2 h-7 w-full rounded-lg border border-[#e7e2ee] bg-white px-2 text-[8px] text-[#26376f] outline-none focus:border-[#8355e4]"
                    placeholder="Informe o ID do contato"
                /></label>
                <div v-else class="flex items-center gap-3 text-[#98a1b6]">
                  <span
                    class="grid size-9 place-items-center rounded-full bg-[#f4f1f7]"
                    ><span class="i-lucide-user-round size-4" /></span><span><strong class="block text-[9px]">{{
                      contact.label
                    }}</strong><small class="mt-1 block text-[8px]">Sem participante disponível</small></span>
                </div>
              </article>
            </div>
            <button
              type="button"
              disabled
              class="mt-3 flex h-10 w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-dashed border-[#d8d2e2] text-[9px] font-semibold text-[#9aa2b4]"
            >
              <span class="i-lucide-users-round size-4" /> Participantes
              adicionais indisponíveis
            </button>
            <article
              class="mt-[52px] min-h-[95px] rounded-xl bg-gradient-to-br from-[#f4efff] to-[#fbf5ff] p-4 ring-1 ring-[#e7dcff]"
            >
              <div class="flex items-start gap-3">
                <span
                  class="grid size-7 shrink-0 place-items-center rounded-full bg-[#5e25d0] text-white"
                  ><span class="i-lucide-circle-help size-4" /></span><span><strong class="block text-[10px] text-[#4c1bc5]">Pontuação da visita</strong><small class="mt-1 block text-[8px] leading-4 text-[#6b759a]">Não retornada pela API de criação. O agendamento será feito
                    sem estimativa de pontos.</small></span>
              </div>
            </article>
          </section>
        </div>

        <div
          class="relative -top-[5px] -ml-[33px] grid w-[calc(100%+2rem)] grid-cols-[minmax(0,1fr)_293px] items-start gap-[14px]"
        >
          <section
            class="relative h-[137px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white px-4 pb-3 pt-1.5 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
          >
            <h2 class="text-[12px] font-bold">
              Escolha a data e o melhor horário
            </h2>
            <p class="sr-only">
              Informe o período que será enviado à API da visita
            </p>
            <div class="mt-2 grid grid-cols-2 gap-2">
              <label
                v-for="field in scheduleFields"
                :key="field.key"
                class="block rounded-lg border border-[#e9e4f0] bg-[#fcfbfd] px-2 py-1"
                ><span class="block truncate text-[7px] font-semibold text-[#66739b]">{{ field.label }}</span><input
                  v-model="model.form[field.key]"
                  type="datetime-local"
                  class="mt-0.5 h-7 w-full bg-transparent px-0 text-[8px] text-[#26376f] outline-none focus:border-[#8051e2]"
              /></label>
            </div>
            <div v-if="configurationFields.length" class="mt-2">
              <p class="text-[8px] font-semibold text-[#101958]">Configurações disponíveis</p>
              <div class="mt-1 grid grid-cols-3 gap-2">
                <label
                  v-for="field in configurationFields"
                  :key="field.key"
                  class="block min-w-0"
                  ><span class="sr-only">{{ field.label }}</span><select
                    v-model="model.form[field.key]"
                    class="h-7 w-full rounded-lg border border-[#e9e4f0] bg-[#fcfbfd] px-2 text-[8px] text-[#26376f] outline-none focus:border-[#8051e2]"
                  >
                    <option
                      v-for="option in field.options || []"
                      :key="option"
                      :value="option"
                    >
                      {{ optionLabel(option) }}
                    </option>
                  </select></label>
              </div>
            </div>
            <p
              v-if="creationError"
              aria-live="polite"
              class="absolute bottom-1 left-4 right-4 truncate text-[7px] text-rose-700"
            >
              {{ creationError }}
            </p>
            <p
              v-else-if="model.notice"
              aria-live="polite"
              class="absolute bottom-1 left-4 right-4 truncate text-[7px] text-emerald-700"
            >
              {{ model.notice }}
            </p>
          </section>

          <section
            class="h-[137px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white px-4 pb-4 pt-1.5 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
          >
            <h2 class="text-[12px] font-bold">Resumo da visita</h2>
            <dl class="mt-3 space-y-3 text-[9px]">
              <div class="flex items-start gap-2">
                <dt class="i-lucide-building-2 size-3 text-[#63719a]" />
                <dd>
                  <small class="block text-[#7a84a4]">Imóvel</small><strong class="mt-1 block">{{
                    selectedProperty?.title ||
                    (model.form.imovel_id
                      ? `Imóvel #${model.form.imovel_id}`
                      : "Aguardando seleção")
                  }}</strong>
                </dd>
              </div>
              <div class="flex items-start gap-2">
                <dt class="i-lucide-calendar-days size-3 text-[#63719a]" />
                <dd>
                  <small class="block text-[#7a84a4]">Data e hora</small><strong class="mt-1 block">{{
                    formatDateTime(model.form.scheduled_at)
                  }}</strong>
                </dd>
              </div>
              <div class="flex items-start gap-2">
                <dt class="i-lucide-users-round size-3 text-[#63719a]" />
                <dd>
                  <small class="block text-[#7a84a4]">Participante</small><span class="mt-1 flex items-center gap-2"><img
                      v-if="selectedContact?.image"
                      :src="selectedContact.image"
                      :alt="selectedContact.name"
                      class="size-7 rounded-full border-2 border-white object-cover"
                    /><span
                      v-else
                      class="grid size-7 place-items-center rounded-full bg-[#f0ebff] text-[8px] font-bold text-[#5420c8]"
                      >{{
                        initials(selectedContact?.name || model.form.contact_id)
                      }}</span><strong>{{
                      selectedContact?.name ||
                      (model.form.contact_id
                        ? `Contato #${model.form.contact_id}`
                        : "Aguardando seleção")
                    }}</strong></span>
                </dd>
              </div>
            </dl>
          </section>
        </div>
      </section>

      <aside
        data-imudei-visual-id="visit-create-sidebar"
        class="-ml-0.5 -mt-4 space-y-4"
      >
        <section
          class="h-[292px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white p-4 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[12px] font-bold">Visitas recentes</h2>
            <span class="text-[9px] font-bold text-[#5520c8]">{{
              recentVisits.length ? recentVisits.length : "—"
            }}</span>
          </header>
          <div
            v-if="recentVisits.length"
            class="mt-3 divide-y divide-[#f0edf5]"
          >
            <article
              v-for="visit in recentVisits"
              :key="visit.id"
              class="flex items-center gap-2 py-3"
            >
              <span
                class="grid size-8 place-items-center rounded-full bg-[#f1ecff] text-[#5821ca]"
                ><span class="i-lucide-calendar-check-2 size-3.5" /></span><span class="min-w-0 flex-1"><strong class="block truncate text-[9px]">{{
                  visit.title
                }}</strong><small class="mt-1 block truncate text-[8px] text-[#647096]">{{
                  visit.subtitle || "Informação não retornada"
                }}</small></span><small class="self-start text-[8px] text-[#8490ab]">{{
                visit.status || "—"
              }}</small>
            </article>
          </div>
          <div
            v-else
            class="mt-5 flex h-[218px] flex-col items-center justify-center rounded-lg bg-[#faf9fc] px-5 text-center"
          >
            <span
              class="grid size-9 place-items-center rounded-full bg-[#f0edf5] text-[#9ba4b8]"
              ><span class="i-lucide-calendar-x-2 size-4" /></span><strong class="mt-3 text-[10px] text-[#59668c]">Visitas não retornadas</strong><small class="mt-1 text-[8px] leading-4 text-[#8790a7]">Este espaço será preenchido quando a API disponibilizar a
              agenda.</small>
          </div>
        </section>
        <section
          class="h-[126px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white px-3 pb-3 pt-2.5 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[12px] font-bold">Operação de visitas</h2>
            <span
              class="i-lucide-chart-no-axes-combined size-4 text-[#5520c8]"
            />
          </header>
          <div
            class="mt-3 h-[70px] rounded-lg border border-[#f0ebf4] bg-[#faf8fd] px-3 py-2"
          >
            <div class="flex items-center gap-2">
              <span
                class="grid size-7 shrink-0 place-items-center rounded-md bg-violet-100 text-[#6d38d1]"
              >
                <span class="i-lucide-calendar-days size-3.5" />
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[9px]">{{
                  visitMetrics[0]?.label || "Agenda"
                }}</strong>
                <small class="mt-0.5 block truncate text-[7px] text-[#687394]">{{ visitMetrics[3]?.value || "—" }}
                  {{
                    visitMetrics[3]?.label?.toLocaleLowerCase("pt-BR") ||
                    "aguardando"
                  }}</small>
              </span>
              <strong class="text-[11px] text-[#4f1ec0]">{{ visitMetrics[1]?.value || "—" }}/{{
                  visitMetrics[0]?.value || "—"
                }}</strong>
            </div>
            <div class="mt-3 flex items-center gap-2">
              <span class="h-1 flex-1 rounded-full bg-[#e7e1f1]" />
              <small class="text-[7px] text-[#687394]">{{ visitMetrics[2]?.value || "—" }}
                {{
                  visitMetrics[2]?.label?.toLocaleLowerCase("pt-BR") ||
                  "concluídas"
                }} · Pontos indisponíveis</small>
            </div>
          </div>
        </section>
        <section
          class="h-[125px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white p-4 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[12px] font-bold">Conquistas</h2>
            <span class="text-[8px] font-semibold text-[#8490aa]">Indisponíveis</span>
          </header>
          <div class="mt-5 grid grid-cols-4 gap-2">
            <span
              v-for="index in 4"
              :key="index"
              class="grid h-11 place-items-center rounded-xl border border-dashed border-[#e4dfec] bg-[#faf9fc] text-[#a8b0c1]"
              ><span class="i-lucide-award size-5" /></span>
          </div>
          <p class="mt-2 text-center text-[7px] text-[#8490aa]">
            Conquistas não retornadas pela API.
          </p>
        </section>
        <section
          class="h-[264px] overflow-hidden rounded-xl border border-[#ebe7f2] bg-white p-4 shadow-[0_3px_14px_rgba(55,30,110,0.03)]"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[12px] font-bold">Ranking da semana</h2>
            <span class="i-lucide-trophy size-4 text-[#c3a662]" />
          </header>
          <p class="sr-only">
            O ranking não foi retornado pela API de visitas.
          </p>
          <div class="mt-3 space-y-1">
            <div
              v-for="index in 5"
              :key="index"
              class="flex h-9 items-center gap-2 rounded-lg px-2"
              :class="index === 2 ? 'bg-[#f5f0ff]' : ''"
            >
              <span class="w-3 text-center text-[8px] text-[#9da5b7]">—</span><span
                class="grid size-6 place-items-center rounded-full bg-[#f1eef5] text-[#a9b0bf]"
                ><span class="i-lucide-user-round size-3" /></span><span class="min-w-0 flex-1"><small
                  v-if="index === 1"
                  class="block truncate text-[8px] text-[#7c86a1]"
                  >Ranking não retornado pela API.</small><small v-else class="block h-px bg-[#eeeaf3]" /></span><span class="text-[8px] text-[#9da5b7]">—</span>
            </div>
          </div>
        </section>
      </aside>
    </div>

    <footer
      data-imudei-visual-id="visit-create-footer"
      class="fixed bottom-[7px] left-[200px] right-[18px] z-40 flex h-[50px] items-center gap-5 rounded-lg border border-[#eeeaf3] bg-white px-7 shadow-[0_-3px_15px_rgba(45,25,90,0.06)]"
    >
      <button
        type="button"
        class="h-8 rounded-lg border border-[#f3b5c6] px-7 text-[10px] font-semibold text-rose-600"
        :disabled="saving"
        @click="emit('navigate', 'dashboard')"
      >
        Cancelar
      </button>
      <div
        class="flex flex-1 items-center justify-center gap-3 text-[9px] text-[#637096]"
      >
        <span class="flex items-center gap-1 font-semibold text-[#26366f]"><i
            class="grid size-5 place-items-center rounded-full"
            :class="
              steps[0].complete
                ? 'bg-emerald-100 text-emerald-600'
                : 'bg-[#e8e8ed] text-[#a5a8b3]'
            "
            >✓</i>Agendar</span><i class="h-px w-16 bg-[#d9d7df]" /><span
          class="flex items-center gap-1"
          ><i
            class="grid size-5 place-items-center rounded-full bg-[#e8e8ed] text-[#a5a8b3]"
            >✓</i>Confirmar</span><i class="h-px w-16 bg-[#d9d7df]" /><span
          class="flex items-center gap-1 text-[#9da4b6]"
          ><i
            class="grid size-5 place-items-center rounded-full bg-[#e8e8ed] text-[#a5a8b3]"
            >◉</i>Resultado</span>
      </div>
      <button
        type="button"
        class="flex h-8 items-center gap-3 rounded-lg bg-gradient-to-r from-[#4d1abf] to-[#a72de3] px-5 text-[10px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="saving || !canCreateVisit"
        @click="createVisit"
      >
        {{ saving ? "Agendando…" : "Agendar visita" }}
        <span class="rounded bg-white/20 px-2 py-0.5">{{
          model.canCreate ? "API" : "Indisponível"
        }}</span>
      </button>
    </footer>
  </main>
</template>
